import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import redditApi from '../../services/redditApi';

const RedditAuthContext = createContext(null);

export function RedditAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearTokens = useCallback(() => {
    localStorage.removeItem('reddit_access_token');
    localStorage.removeItem('reddit_refresh_token');
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, [clearTokens]);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem('reddit_access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await redditApi.get('/auth/me/');
      setUser(data);
    } catch {
      const refresh = localStorage.getItem('reddit_refresh_token');
      if (refresh) {
        try {
          const { data: tokens } = await redditApi.post('/auth/token/refresh/', { refresh });
          if (tokens.access) {
            localStorage.setItem('reddit_access_token', tokens.access);
            const { data: me } = await redditApi.get('/auth/me/');
            setUser(me);
          } else {
            clearTokens();
            setUser(null);
          }
        } catch {
          clearTokens();
          setUser(null);
        }
      } else {
        clearTokens();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [clearTokens]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await redditApi.post('/auth/token/', {
      username: email.trim().toLowerCase(),
      // Normalize / strip invisible chars that can sneak in from paste/autofill.
      password: password
        .normalize('NFKC')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .trim(),
    });
    localStorage.setItem('reddit_access_token', data.access);
    if (data.refresh) localStorage.setItem('reddit_refresh_token', data.refresh);
    const { data: me } = await redditApi.get('/auth/me/');
    setUser(me);
    return me;
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser: loadMe }),
    [user, loading, login, logout, loadMe],
  );

  return <RedditAuthContext.Provider value={value}>{children}</RedditAuthContext.Provider>;
}

export function useRedditAuth() {
  const ctx = useContext(RedditAuthContext);
  if (!ctx) throw new Error('useRedditAuth must be used within RedditAuthProvider');
  return ctx;
}
